import { useEffect, useState } from 'react'

/**
 * The `useDebounce` function takes a value and a delay as input, and returns a debounced value that is
 * updated after the specified delay.
 * @param {T} value - The value that needs to be debounced.
 * @param {number} delay - The delay parameter is the amount of time (in milliseconds) that the
 * function should wait before updating the debounced value.
 * @returns The `useDebounce` hook returns the debounced value of the input `value`. The debounced
 * value is updated after a specified `delay` period has elapsed since the last update to the input
 * `value`. The returned value is of the same type as the input `value`.
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cancel the timeout if value changes (also on delay change or unmount)
    // This is how we prevent debounced value from updating if value is changed ...
    // .. within the delay period. Timeout gets cleared and restarted.
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
